/* eslint-disable */
/* ---------- TOPBAR — global sticky nav ----------
   Exports: Topbar
   Mounted in app.jsx (homepage React shell only).
   Static .html sub-pages get topbar in a later round.
   Active-route: matches pathname prefix, highlights with var(--amber).
   Nav set (9): home · about · uses · products · posts · speak · press · work with me · contact.
   Mobile: condense font/padding ≤640px, then flex-wrap to a 2nd row ≤520px.
   8 items can't fit one 320px row, so they WRAP — never clip, never scroll.
   scrollWidth stays == innerWidth (hard brand rule).
   ------------------------------------------------------- */

import React, { useState as _useStateT, useEffect as _useEffectT } from 'react';

const NAV_ITEMS = [
  { label: "home",     href: "/" },
  { label: "about",    href: "/about" },
  { label: "uses",     href: "/uses" },
  { label: "products", href: "/products" },
  { label: "posts",    href: "/posts" },
  { label: "speak",    href: "/speak" },
  { label: "press",    href: "/press" },
  { label: "work with me", href: "/work-with-me" },
  { label: "contact",  href: "/#contact" },
];

/* Always-visible product rail — one link per product description page.
   Brand colors locked per BRAND.md (Commander = coral, commanderplugin.com). */
const PRODUCT_ITEMS = [
  { label: "commander",      href: "/products/commander",      c: "#FF6B47" },
  { label: "readyiq",        href: "/products/readyiq",        c: "#7C3AED" },
  { label: "dmhub",          href: "/products/dmhub",          c: "#00DC82" },
  { label: "mywifi",         href: "/products/mywifi",         c: "#0096FF" },
  { label: "guest networks", href: "/products/guest-networks", c: "#FF1E56" },
];

/* Determine active nav item by current pathname */
function getActiveHref(pathname) {
  if (pathname === "/" || pathname === "") return "/";
  for (const item of NAV_ITEMS) {
    const href = item.href;
    if (href === "/") continue;
    // Strip hash anchor for comparison
    const cleanHref = href.replace(/#.*$/, "");
    if (cleanHref && pathname.startsWith(cleanHref)) return href;
  }
  return "/";
}

function Topbar() {
  const [active, _setActive] = _useStateT(
    () => typeof window !== "undefined" ? getActiveHref(window.location.pathname) : "/"
  );

  /* Update active on popstate (SPA nav) */
  _useEffectT(() => {
    const onPop = () => _setActive(getActiveHref(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  /* Scroll beacon — 2px phosphor progress line under the nav.
     Scroll-linked (not autonomous), so it stays on under reduced motion.
     rAF-throttled; writes transform directly to skip React re-renders. */
  const beaconRef = React.useRef(null);
  _useEffectT(() => {
    let raf = 0;
    const update = () => {
      raf = 0;
      const el = beaconRef.current;
      if (!el) return;
      const max = document.documentElement.scrollHeight - window.innerHeight;
      const p = max > 0 ? Math.min(1, window.scrollY / max) : 0;
      el.style.transform = `scaleX(${p})`;
    };
    const onScroll = () => { if (!raf) raf = requestAnimationFrame(update); };
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll, { passive: true });
    update();
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (raf) cancelAnimationFrame(raf);
    };
  }, []);

  return (
    <nav
      className="kz-topbar-nav"
      aria-label="Site navigation"
      style={{
        position: "sticky",
        top: 0,
        zIndex: 60,
        background: "var(--bg, #000)",
        borderBottom: "1px solid var(--line, rgba(232,230,220,0.10))",
        fontFamily: "var(--mono, monospace)",
      }}
    >
      <style>{`
        .kz-topbar-nav-inner {
          display: flex;
          align-items: center;
          gap: 0;
          padding: 0 24px;
          height: 38px;
          min-width: 0;
          max-width: 100%;
          box-sizing: border-box;
        }
        .kz-topbar-nav-sigil {
          flex: none;
          width: 24px;
          height: 24px;
          border: 1px solid var(--amber, #f5f3ec);
          color: var(--amber, #f5f3ec);
          display: inline-flex;
          align-items: center;
          justify-content: center;
          font-weight: 700;
          font-size: 10px;
          letter-spacing: 0;
          text-decoration: none;
          margin-right: 14px;
        }
        .kz-topbar-nav-sigil:hover {
          background: color-mix(in oklch, var(--amber, #f5f3ec) 12%, transparent);
        }
        .kz-topbar-nav-links {
          display: flex;
          align-items: center;
          gap: 0;
          min-width: 0;
          flex: 1;
        }
        .kz-topbar-nav-link {
          display: inline-flex;
          align-items: center;
          padding: 0 10px;
          height: 38px;
          font-size: 11px;
          letter-spacing: 0.16em;
          text-transform: lowercase;
          color: var(--fg-3, rgba(232,230,220,0.45));
          text-decoration: none;
          white-space: nowrap;
          border-bottom: 2px solid transparent;
          transition: color 120ms ease, border-color 120ms ease;
          flex: none;
        }
        .kz-topbar-nav-link:hover {
          color: var(--fg, #e8e6dc);
        }
        .kz-topbar-nav-link.is-active {
          color: var(--amber, #f5f3ec);
          border-bottom-color: var(--amber, #f5f3ec);
        }
        /* 8 items: condense on tablet, then WRAP on phone so nothing ever
           exceeds the viewport width (scrollWidth must equal innerWidth). */
        @media (max-width: 640px) {
          .kz-topbar-nav-inner { padding: 0 12px; }
          .kz-topbar-nav-sigil { margin-right: 8px; }
          .kz-topbar-nav-link { padding: 0 7px; font-size: 10px; letter-spacing: 0.08em; }
        }
        @media (max-width: 520px) {
          /* Wrap to a second row instead of clipping or overflowing. */
          .kz-topbar-nav-inner { height: auto; align-items: flex-start; padding: 7px 12px; }
          .kz-topbar-nav-sigil { margin-top: 4px; }
          .kz-topbar-nav-links { flex-wrap: wrap; row-gap: 2px; }
          .kz-topbar-nav-link { height: 30px; padding: 0 6px; font-size: 10px; letter-spacing: 0.06em; }
        }
        @media (max-width: 360px) {
          .kz-topbar-nav-link { padding: 0 5px; font-size: 9px; }
        }
        .kz-scroll-beacon {
          position: absolute;
          left: 0; right: 0; bottom: -1px;
          height: 2px;
          background: var(--amber, #f5f3ec);
          box-shadow: 0 0 8px color-mix(in oklch, var(--amber, #f5f3ec) 55%, transparent);
          transform: scaleX(0);
          transform-origin: left center;
          pointer-events: none;
          will-change: transform;
        }
        .kz-product-rail {
          display: flex;
          align-items: center;
          gap: 0;
          padding: 0 24px;
          height: 26px;
          border-top: 1px solid var(--line, rgba(232,230,220,0.10));
          background: var(--bg-1, #050505);
          overflow-x: auto;
          scrollbar-width: none;
          -webkit-overflow-scrolling: touch;
        }
        .kz-product-rail::-webkit-scrollbar { display: none; }
        .kz-product-rail-label {
          flex: none;
          font-size: 9px;
          letter-spacing: 0.2em;
          text-transform: uppercase;
          color: var(--fg-4, #7c7969);
          margin-right: 12px;
          white-space: nowrap;
        }
        .kz-product-rail-link {
          flex: none;
          display: inline-flex;
          align-items: center;
          gap: 6px;
          padding: 0 10px;
          height: 26px;
          font-size: 10px;
          letter-spacing: 0.12em;
          text-transform: lowercase;
          color: var(--fg-3, rgba(232,230,220,0.45));
          text-decoration: none;
          white-space: nowrap;
          transition: color 120ms ease;
        }
        .kz-product-rail-link i {
          width: 6px; height: 6px;
          background: var(--prc);
          font-style: normal;
          flex: none;
        }
        .kz-product-rail-link:hover { color: var(--prc); }
        @media (max-width: 640px) {
          .kz-product-rail { padding: 0 12px; }
          .kz-product-rail-link { padding: 0 7px; font-size: 9px; letter-spacing: 0.06em; }
        }
      `}</style>
      <div className="kz-topbar-nav-inner">
        <a
          href="/"
          className="kz-topbar-nav-sigil"
          aria-label="kevinz.ai home"
        >
          KZ
        </a>
        <div className="kz-topbar-nav-links" role="list">
          {NAV_ITEMS.map((item) => {
            const isActive = active === item.href;
            return (
              <a
                key={item.href}
                href={item.href}
                className={"kz-topbar-nav-link" + (isActive ? " is-active" : "")}
                aria-current={isActive ? "page" : undefined}
                role="listitem"
              >
                {item.label}
              </a>
            );
          })}
        </div>
      </div>
      <div className="kz-product-rail" aria-label="Products">
        <span className="kz-product-rail-label">products ▸</span>
        {PRODUCT_ITEMS.map((p) => (
          <a key={p.href} href={p.href} className="kz-product-rail-link" style={{ "--prc": p.c }}>
            <i aria-hidden="true" />{p.label}
          </a>
        ))}
      </div>
      <div className="kz-scroll-beacon" ref={beaconRef} aria-hidden="true" />
    </nav>
  );
}

Object.assign(window, { Topbar });

export { Topbar };
